import { useEffect, useMemo, useState } from 'react';
import { FiBell, FiClock, FiSettings } from 'react-icons/fi';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { membersApi, portalSettingsApi } from '../services/api';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'UTC', label: 'UTC' },
];

export default function PortalSettings() {
  const { selectedPortal } = useHubSpotPortals();
  const portalId = selectedPortal?.portal_id;

  const [owners, setOwners] = useState([]);
  const [fallbackOwnerId, setFallbackOwnerId] = useState('');
  const [capTimezone, setCapTimezone] = useState('America/New_York');
  const [healthAlertCompanies, setHealthAlertCompanies] = useState([]);
  const [healthAlertProperties, setHealthAlertProperties] = useState([]);
  const [healthAlertCompanyId, setHealthAlertCompanyId] = useState('');
  const [healthAlertPropertyName, setHealthAlertPropertyName] = useState('');
  const [healthAlertPropertyLabel, setHealthAlertPropertyLabel] = useState('');
  const [healthAlertEnabled, setHealthAlertEnabled] = useState(false);
  const [healthAlertOptionsError, setHealthAlertOptionsError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const ownerNameById = useMemo(() => {
    const map = new Map();
    owners.forEach((owner) => {
      const ownerId = owner.id || owner.hs_owner_id;
      if (!ownerId) return;
      const firstName = owner.firstName || owner.first_name || '';
      const lastName = owner.lastName || owner.last_name || '';
      const label = `${firstName} ${lastName}`.trim() || owner.email || owner.name || ownerId;
      map.set(String(ownerId), label);
    });
    return map;
  }, [owners]);

  useEffect(() => {
    if (!portalId) {
      setOwners([]);
      setFallbackOwnerId('');
      setCapTimezone('America/New_York');
      setHealthAlertCompanies([]);
      setHealthAlertProperties([]);
      setHealthAlertCompanyId('');
      setHealthAlertPropertyName('');
      setHealthAlertPropertyLabel('');
      setHealthAlertEnabled(false);
      return;
    }

    let isMounted = true;

    const loadSettings = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const [ownersResult, settingsResult] = await Promise.all([
          membersApi.fetchHubspotOwners(portalId),
          portalSettingsApi.fetchPortalSettings(portalId),
        ]);

        if (!isMounted) {
          return;
        }

        const ownerList = ownersResult?.owners || ownersResult?.data || ownersResult || [];
        ownerList.sort((a, b) => {
          const aLabel = (a.firstName || a.first_name || '').toLowerCase();
          const bLabel = (b.firstName || b.first_name || '').toLowerCase();
          return aLabel.localeCompare(bLabel);
        });
        setOwners(ownerList);

        const settings = settingsResult?.settings;
        setFallbackOwnerId(settings?.fallback_owner_id || '');
        setCapTimezone(settings?.cap_timezone || 'America/New_York');
        setHealthAlertCompanyId(settings?.health_alert_company_id || '');
        setHealthAlertPropertyName(settings?.health_alert_property_name || '');
        setHealthAlertPropertyLabel(settings?.health_alert_property_label || '');
        setHealthAlertEnabled(Boolean(Number(settings?.health_alert_enabled || 0)));

        try {
          const options = await portalSettingsApi.fetchHealthAlertOptions(portalId);
          if (!isMounted) return;
          setHealthAlertCompanies(options?.companies || []);
          setHealthAlertProperties(options?.properties || []);
          setHealthAlertOptionsError(null);
        } catch (optionsError) {
          console.error('Failed to load HubSpot health alert options:', optionsError);
          if (isMounted) {
            setHealthAlertCompanies([]);
            setHealthAlertProperties([]);
            setHealthAlertOptionsError(
              'Connect or reauthorize Distributely Agentic Tools with HubSpot company and company-property read access.'
            );
          }
        }
      } catch (err) {
        console.error('Failed to load portal settings:', err);
        if (isMounted) {
          setError('Failed to load portal settings. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [portalId]);

  const handleSave = async () => {
    if (!portalId) {
      setError('Select a portal before saving settings.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (healthAlertEnabled && (!healthAlertCompanyId || !healthAlertPropertyName)) {
        setError('Enter a HubSpot company ID and select a writable text property before enabling health alerts.');
        return;
      }
      await portalSettingsApi.updatePortalSettings(portalId, {
        fallbackOwnerId,
        capTimezone,
        healthAlertCompanyId,
        healthAlertPropertyName,
        healthAlertEnabled,
      });
      const ownerLabel = fallbackOwnerId ? ownerNameById.get(String(fallbackOwnerId)) || fallbackOwnerId : 'None';
      setSuccess(
        `Portal settings saved. Fallback owner: ${ownerLabel}. Health alerts: ${healthAlertEnabled ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      console.error('Failed to update portal settings:', err);
      setError('Failed to update portal settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <FiSettings className="w-6 h-6 text-gray-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Portal Settings</h1>
        </div>
        <p className="text-gray-600">
          Configure portal-wide routing and capping defaults.
        </p>
        {selectedPortal && (
          <p className="text-sm text-blue-700 mt-2">
            Portal:
            <span className="font-semibold"> {selectedPortal.hub_domain}</span>
            <span className="ml-1 text-gray-600">(ID: {selectedPortal.portal_id})</span>
          </p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {loading && <p className="text-gray-500">Loading settings...</p>}
        {!loading && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Fallback Owner
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                value={fallbackOwnerId}
                onChange={(event) => setFallbackOwnerId(event.target.value)}
              >
                <option value="">No fallback owner</option>
                {owners.map((owner) => {
                  const ownerId = owner.id || owner.hs_owner_id;
                  const label = ownerNameById.get(String(ownerId)) || ownerId;
                  return (
                    <option key={ownerId} value={ownerId}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Used only when every eligible team fails to produce an owner.
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center mb-2">
                <FiClock className="w-4 h-4 text-gray-600 mr-2" />
                <label className="block text-sm font-medium text-gray-700">
                  Default Capping Timezone
                </label>
              </div>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                value={capTimezone}
                onChange={(event) => setCapTimezone(event.target.value)}
              >
                {TIMEZONES.map((timezone) => (
                  <option key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Defines daily, weekly, monthly, and quarterly cap boundaries when a member has no
                Distributely timezone. A member-specific Distributely timezone takes precedence.
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center mb-2">
                <FiBell className="w-4 h-4 text-gray-600 mr-2" />
                <h2 className="text-sm font-medium text-gray-700">
                  HubSpot Health Alert Workflow Trigger
                </h2>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Enter the dedicated company record ID and select the writable text property that Distributely
                will use to enroll your company-based workflow. The property receives a temporary
                alert token and should be cleared at the end of the workflow.
              </p>

              {healthAlertOptionsError && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  {healthAlertOptionsError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Company ID
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    list="health-alert-company-options"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={healthAlertCompanyId}
                    onChange={(event) => {
                      const companyId = event.target.value.trim();
                      setHealthAlertCompanyId(companyId);
                    }}
                    placeholder="Enter the HubSpot company record ID"
                  />
                  <datalist id="health-alert-company-options">
                    {healthAlertCompanies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}{company.domain ? ` (${company.domain})` : ''}
                      </option>
                    ))}
                  </datalist>
                  <p className="text-xs text-gray-500 mt-2">
                    Copy the record ID from the company URL in HubSpot. Loaded companies appear as suggestions.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Property
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={healthAlertPropertyName}
                    onChange={(event) => setHealthAlertPropertyName(event.target.value)}
                    disabled={healthAlertProperties.length === 0}
                  >
                    <option value="">Select a writable text property</option>
                    {healthAlertPropertyName && !healthAlertProperties.some((property) => property.name === healthAlertPropertyName) && (
                      <option value={healthAlertPropertyName}>
                        {healthAlertPropertyLabel || healthAlertPropertyName} ({healthAlertPropertyName})
                      </option>
                    )}
                    {healthAlertProperties.map((property) => (
                      <option key={property.name} value={property.name}>
                        {property.label} ({property.name})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="mt-4 inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={healthAlertEnabled}
                  onChange={(event) => setHealthAlertEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Enable inactive HubSpot owner alerts
              </label>
              <p className="text-xs text-gray-500 mt-2">
                Use one dedicated company per portal. Distributely queues notifications so the
                company cannot be enrolled in the same workflow more than once at a time.
              </p>
            </div>
          </>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
            {success}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
